import React, { useState, useEffect } from "react";
import {
    Box, Button, TextField, Typography, Grid, IconButton,
    Paper, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// ================================================================
// SHARED: Attachment Grid (view-only)
// ================================================================
// const AttachmentGrid = ({ files = [], onPreview, height = "140px" }) => {
const AttachmentGrid = ({ files = [], onPreview, height = "140px", rnoId = "" }) => {
    if (!files.length) return null;
    return (
        <Box mt={2}>
            <Grid container spacing={2}>
                {files.map((att, index) => {
                    const src = att?.path || att?.preview || null;

                    const isImage =
                        att?.type?.startsWith("image") ||
                        /\.(jpg|jpeg|png|gif|webp)$/i.test(att?.path || "") ||
                        /\.(jpg|jpeg|png|gif|webp)$/i.test(att?.originalName || "") ||
                        /\.(jpg|jpeg|png|gif|webp)$/i.test(att?.fileName || "");

                    const isPdf =
                        att?.type === "application/pdf" ||
                        /\.pdf$/i.test(att?.path || "") ||
                        /\.pdf$/i.test(att?.originalName || "") ||
                        /\.pdf$/i.test(att?.fileName || "");

                    const displayName = att?.originalName || att?.fileName || att?.name || "File";

                    return (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Paper
                                elevation={2}
                                sx={{
                                    p: 1, borderRadius: 2, position: "relative",
                                    overflow: "hidden", height: "100%",
                                    display: "flex", flexDirection: "column",
                                }}
                            >
                                {src && (
                                    <Tooltip title={isPdf ? "Open PDF" : "Preview Image"}>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                isPdf
                                                    ? window.open(src, "_blank")
                                                    : onPreview && onPreview(src)
                                            }
                                            sx={{
                                                position: "absolute", top: 5, left: 5,
                                                zIndex: 2, background: "#fff",
                                                "&:hover": { background: "#f0f0f0" },
                                            }}
                                        >
                                            {isPdf
                                                ? <PictureAsPdfIcon fontSize="small" color="error" />
                                                : <ZoomInIcon fontSize="small" color="primary" />
                                            }
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {isImage ? (
                                    <img
                                        src={src}
                                        alt={displayName}
                                        onClick={() => src && onPreview && onPreview(src)}
                                        style={{
                                            width: "100%", height,
                                            objectFit: "cover", borderRadius: "8px",
                                            cursor: "pointer",
                                        }}
                                    />
                                ) : isPdf ? (
                                    src?.startsWith("blob:") ? (
                                        <Box
                                            sx={{ height, borderRadius: "8px", overflow: "hidden", cursor: "pointer" }}
                                            onClick={() => window.open(src, "_blank")}
                                        >
                                            <iframe
                                                src={src}
                                                title={displayName}
                                                style={{ width: "100%", height: "100%", border: "none", pointerEvents: "none" }}
                                            />
                                        </Box>
                                    ) : (
                                        <Box
                                            onClick={() => src && window.open(src, "_blank")}
                                            sx={{
                                                height, display: "flex", flexDirection: "column",
                                                alignItems: "center", justifyContent: "center",
                                                background: "#fff3f3", borderRadius: "8px",
                                                cursor: "pointer", border: "1px solid #ffcccc",
                                            }}
                                        >
                                            <PictureAsPdfIcon sx={{ fontSize: 50, color: "#e53935" }} />
                                            <Typography variant="caption" sx={{ color: "#e53935", mt: 0.5, fontWeight: 600 }}>
                                                Open PDF
                                            </Typography>
                                        </Box>
                                    )
                                ) : (
                                    <Box sx={{
                                        height, display: "flex", flexDirection: "column",
                                        alignItems: "center", justifyContent: "center",
                                        background: "#f5f5f5", borderRadius: "8px",
                                    }}>
                                        <ImageIcon sx={{ fontSize: 50, color: "#999" }} />
                                        <Typography variant="caption" sx={{ color: "#999", mt: 0.5 }}>
                                            {displayName.split(".").pop()?.toUpperCase() || "File"}
                                        </Typography>
                                    </Box>
                                )}

                                <Typography
                                    variant="caption"
                                    sx={{ mt: 1, display: "block", textAlign: "center", wordBreak: "break-word" }}
                                >
                                    {att?.originalName || att?.name || att?.fileName || "File"}
                                </Typography>

                                <Button
                                    variant="contained"
                                    size="small"
                                    fullWidth
                                    sx={{ mt: 1 }}
                                    onClick={async () => {
                                        try {
                                            const fileUrl = att?.path
                                                ? att.path
                                                : `${window.Platform.database.funcForBaseURL()}/file/${att.fileName}`;
                                            const response = await fetch(fileUrl);
                                            if (!response.ok) throw new Error("Failed to fetch");
                                            const blob = await response.blob();
                                            const url = window.URL.createObjectURL(blob);
                                            const link = document.createElement("a");
                                            link.href = url;
                                            // link.setAttribute("download", att?.originalName || att?.fileName || `file_${index}`);
                                            const fileName = att?.originalName || att?.fileName || `file_${index}`;
                                            link.setAttribute(
                                                "download",
                                                rnoId ? `SM_${rnoId}_${fileName}` : fileName
                                            );
                                            document.body.appendChild(link);
                                            link.click();
                                            link.remove();
                                            window.URL.revokeObjectURL(url);
                                        } catch (err) {
                                            console.error(err);
                                            window.NotificationUtils.showError("Failed to download file");
                                        }
                                    }}
                                >
                                    ⬇ Download
                                </Button>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};

// ================================================================
// SHARED: Fullscreen image overlay
// ================================================================
const ImagePreviewOverlay = ({ src, onClose }) =>
    src ? (
        <Box
            onClick={onClose}
            sx={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                background: "rgba(0,0,0,0.85)", zIndex: 9999,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "zoom-out",
            }}
        >
            <IconButton onClick={onClose} sx={{ position: "absolute", top: 16, right: 16, color: "#fff" }}>
                <CloseIcon />
            </IconButton>
            <img
                src={src}
                alt="preview"
                style={{ maxWidth: "90%", maxHeight: "90%", borderRadius: 8, objectFit: "contain" }}
            />
        </Box>
    ) : null;

// ================================================================
// SHARED: Dispute Dialog
// ================================================================
const DisputeDialog = ({ state, onChange, onClose, onSubmit }) => (
    <Dialog
        open={state.open}
        onClose={!state.submitting ? onClose : undefined}
        maxWidth="sm"
        fullWidth
    >
        <DialogTitle sx={{ fontWeight: 700 }}>
            Raise Discrepancy
            <IconButton
                onClick={onClose}
                disabled={state.submitting}
                sx={{ position: "absolute", right: 10, top: 10 }}
            >
                <CloseIcon />
            </IconButton>
        </DialogTitle>

        <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Please describe the issue clearly. Remark must be at least 20 characters.
            </Typography>
            <TextField
                fullWidth
                multiline
                label="Remark *"
                placeholder="Enter your discrepancy remark here..."
                value={state.remark}
                onChange={onChange}
                error={!!state.remarkError}
                helperText={state.remarkError}
                sx={{ "& .MuiInputBase-root": { alignItems: "flex-start", minHeight: "120px" } }}
                disabled={state.submitting}
            />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button variant="outlined" onClick={onClose} disabled={state.submitting}>
                Cancel
            </Button>
            <Button
                variant="contained"
                color="error"
                onClick={onSubmit}
                disabled={state.submitting}
                startIcon={
                    state.submitting
                        ? <CircularProgress size={16} color="inherit" />
                        : <ErrorOutlineIcon />
                }
            >
                {state.submitting ? "Submitting..." : "Submit Discrepancy"}
            </Button>
        </DialogActions>
    </Dialog>
);

// ================================================================
// HELPERS
// ================================================================
const makeDisputeState = () => ({
    open: false,
    entryId: null,
    remark: "",
    remarkError: "",
    submitting: false,
});

const makeRemarkChangeHandler = (setter) => (e) => {
    const val = e.target.value;
    setter((prev) => ({
        ...prev,
        remark: val,
        remarkError:
            val.trim().replace(/\s+/g, " ").length < 20
                ? `Remark must be at least 20 characters (${val.trim().replace(/\s+/g, " ").length}/20)`
                : "",
    }));
};

// ================================================================
// MAIN COMPONENT
// ================================================================
const ViewToVerifyInvoiceOlympiadSM = ({ closePopup, modalData, invoiceType }) => {
    const [oldData, setOldData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [verifyingId, setVerifyingId] = useState(null);
    const [dispute, setDispute] = useState(makeDisputeState());

    useEffect(() => {
        if (modalData?.regId) getOldData();
    }, [modalData?.regId]);

    // const getOldData = async () => {
    //     try {
    //         setHistoryLoading(true);
    //         const result = await window.Platform.database.getGenerateInvoiceDetails({
    //             regId: modalData?.regId,
    //             partyId: modalData?.partyId,
    //             invoiceType: "sm",
    //         });
    //         setOldData(result?.status ? result?.data || [] : []);
    //     } catch (error) {
    //         console.log(error);
    //         setOldData([]);
    //     } finally {
    //         setHistoryLoading(false);
    //     }
    // };
    const getOldData = async () => {
        try {
            setHistoryLoading(true);
            const result = await window.Platform.database.getGenerateInvoiceDetails({
                regId: modalData?.regId,
                partyId: modalData?.partyId,
                invoiceType: "sm",
            });

            const allData = result?.status ? result?.data || [] : [];

            // Only show entries that have SM invoice number filled
            // (study_material_invoice_no is the definitive marker of an SM entry)
            const smData = allData.filter(
                (item) => item?.study_material_invoice_no && item.study_material_invoice_no.toString().trim() !== ""
            );

            setOldData(smData);
        } catch (error) {
            console.log(error);
            setOldData([]);
        } finally {
            setHistoryLoading(false);
        }
    };
    // ── Verify entry ──
    const handleVerify = async (item) => {
        if (!window.confirm("Are you sure you want to verify this entry?")) return;
        try {
            setVerifyingId(item?.id);
            const result = await window.Platform.database.updateInvoicetatus({
                invoice_id: item?.id,
                action: "verify",
                invoiceType: "sm",
            });
            if (result?.status) {
                window.NotificationUtils.showSuccess(result?.message || "Verified successfully");
                closePopup();
            } else {
                window.NotificationUtils.showError(result?.message || "Something went wrong");
            }
        } catch (error) {
            console.log(error);
            window.NotificationUtils.showError("Something went wrong");
        } finally {
            setVerifyingId(null);
        }
    };

    // ── Dispute entry ──
    const openDispute = (item) => setDispute({ ...makeDisputeState(), open: true, entryId: item?.id });
    const closeDispute = () => setDispute(makeDisputeState());
    const handleRemarkChange = makeRemarkChangeHandler(setDispute);

    const handleDisputeSubmit = async () => {
        const trimmed = dispute.remark.trim();
        if (!trimmed) {
            setDispute((p) => ({ ...p, remarkError: "Remark is required" }));
            return;
        }
        if (trimmed.length < 20) {
            setDispute((p) => ({ ...p, remarkError: `Remark must be at least 20 characters (${trimmed.length}/20)` }));
            return;
        }
        try {
            setDispute((p) => ({ ...p, submitting: true }));
            const result = await window.Platform.database.updateInvoicetatus({
                invoice_id: dispute.entryId,
                action: "despute",
                despute_remark_sm: trimmed,
                invoiceType: "sm",
            });
            if (result?.status) {
                window.NotificationUtils.showSuccess(result?.message || "Discrepancy raised successfully");
                closeDispute();
                closePopup();
            } else {
                window.NotificationUtils.showError(result?.message || "Something went wrong");
                setDispute((p) => ({ ...p, submitting: false }));
            }
        } catch (error) {
            console.log(error);
            window.NotificationUtils.showError("Something went wrong");
            setDispute((p) => ({ ...p, submitting: false }));
        }
    };

    // ════════════════════════════════════════════════════════════
    // RENDER
    // ════════════════════════════════════════════════════════════
    return (
        <Box p={2} position="relative">
            <ImagePreviewOverlay src={previewImage} onClose={() => setPreviewImage(null)} />

            {/* Header */}
            <Typography variant="h5" fontWeight={700} textAlign="center" mb={1}>
                Verify Invoice
            </Typography>
            <Box sx={{ borderBottom: "1px solid #000", mb: 2, pb: 1 }}>
                <Typography sx={{ fontSize: "15px" }}>
                    <b>RNO ID:</b> {modalData?.rno_id} |
                    <b style={{ marginLeft: 10 }}>RM Name:</b> {modalData?.userName} |
                    <b style={{ marginLeft: 10 }}>Party Name:</b> {modalData?.schoolName} ({modalData?.partyId})
                </Typography>
            </Box>
            <IconButton onClick={closePopup} sx={{ position: "absolute", top: 12, right: 12 }}>
                <CloseIcon />
            </IconButton>

            <Paper
                elevation={2}
                sx={{ p: 2.5, mb: 3, background: "#fafafa", border: "1px solid #e0e0e0", borderRadius: 2 }}
            >
                <Box mb={2}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth size="small" disabled
                                label="Study Material Number"
                                value={oldData[0]?.study_material_invoice_no || ""}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth size="small" disabled
                                label="Study Material Amount"
                                value={oldData[0]?.study_material_amount || ""}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth size="small" disabled
                                type="date"
                                label="Study Material Date"
                                value={
                                    oldData[0]?.study_material_invoice_date
                                        ? oldData[0].study_material_invoice_date.split("T")[0]
                                        : ""
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    <Box mt={2}>
                        <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
                            Uploaded Study Material Attachments
                        </Typography>
                        <AttachmentGrid
                            files={oldData[0]?.study_material_attachment || []}
                            onPreview={setPreviewImage}
                            height="160px"
                            rnoId={modalData?.rno_id}
                        />
                    </Box>

                </Box>
            </Paper>


            <DisputeDialog
                state={dispute}
                onChange={handleRemarkChange}
                onClose={closeDispute}
                onSubmit={handleDisputeSubmit}
            />
        </Box>
    );
};

export default ViewToVerifyInvoiceOlympiadSM;